import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';
import { WarningOutlined } from '@ant-design/icons';
import { Layout, Menu } from 'antd';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { withRouter, WithRouterProps } from '../withRouter';
import { getSelectedItems } from '../../selectors/catalogue/catalogueSelector';
import * as actions from '../../actions/app/navigationBar';
import { navigationBarData } from '../../constants/ParamountReactConstants';
import { isReceiver } from '../../utils/UserUtils';
import { hasPermission } from '../../utils/Permissions';
import { ApplicationState } from '../../reducers';
import { UserType, UserPermissionRole } from '../../../types/user';
import { SegmentScore } from '../../../types/analyses';
import { Item } from '../../../types/item';
import { ReplitIntegration } from '../../reducers/tools/ReplitReducer';
import { CustomAppConfig } from '../../../types/customApps';
import { getCustomAppsForLevel, CustomAppWithDisplay } from '../../utils/CustomAppsUtils';

const { Sider } = Layout;

interface BrandProductsSideBarProps extends WithRouterProps {
  isReceiver?: boolean;
  user?: UserType;
  roles: UserPermissionRole[];
  segmentScores: SegmentScore[];
  selectedItems: Item[];
  integrations: ReplitIntegration[];
  customAppConfigs: CustomAppConfig[];
  dispatch: Dispatch;
}

class BrandProductsSideBar extends React.Component<BrandProductsSideBarProps> {
  getCustomAppsForProduct = (): CustomAppWithDisplay[] => {
    const { integrations, customAppConfigs, user } = this.props;
    const canAccessCustomApps = user && hasPermission(user, 'can_access_replit');

    if (!canAccessCustomApps) {
      return [];
    }

    return getCustomAppsForLevel(integrations, customAppConfigs, 'PRODUCT');
  };

  getHiddenSegments = (): string[] => {
    const { user, roles } = this.props;
    if (!user || user.admin) return [];
    const userRoles = roles.filter(role => user.user_role_ids.includes(role.id));
    const segmentPermissions = userRoles.flatMap(role =>
      role.segments.map(segment => segment.segment_id)
    );

    return navigationBarData
      .filter(segment => segment.id && !segmentPermissions.includes(segment.id))
      .map(segment => segment.key);
  };

  scoreStyleClass = (segmentId?: number): React.ReactElement | string => {
    const { segmentScores, selectedItems } = this.props;

    if (!segmentId) return '';

    let currentSegmentScore = segmentScores.find(
      segmentScore => segmentScore.segment_id === segmentId
    );

    if (!currentSegmentScore || selectedItems.length === 0) return '';

    // show in description also marketing copy score
    if (segmentId === 4) {
      // description
      const marketingCopySegmentScore = segmentScores.find(
        segmentScore => segmentScore.segment_id === 11
      ); // marketing copy
      currentSegmentScore = {
        error_count:
          marketingCopySegmentScore &&
          marketingCopySegmentScore.error_count > currentSegmentScore.error_count
            ? marketingCopySegmentScore.error_count
            : currentSegmentScore.error_count,
        warning_count:
          marketingCopySegmentScore &&
          marketingCopySegmentScore.warning_count > currentSegmentScore.warning_count
            ? marketingCopySegmentScore.warning_count
            : currentSegmentScore.warning_count,
        info_count:
          marketingCopySegmentScore &&
          marketingCopySegmentScore.info_count > currentSegmentScore.info_count
            ? marketingCopySegmentScore.info_count
            : currentSegmentScore.info_count,
      } as SegmentScore;
    }

    let color = '';
    if (currentSegmentScore.error_count > 0) {
      color = '#f5222d';
    } else if (currentSegmentScore.warning_count > 0) {
      color = '#fa8c16';
    } else if (currentSegmentScore.info_count > 0) {
      color = '#188ca6';
    }

    return color ? <WarningOutlined style={{ color }} /> : '';
  };

  handleMenuClick = (key: string): void => {
    this.props.dispatch(actions.setActiveArea(key));
  };

  render() {
    const { location, isReceiver, user } = this.props;

    if (!user) {
      return <Sider theme="light" className="sidebar-brand-products" />;
    }

    const canManageCustomFields = isReceiver && hasPermission(user, 'can_manage_custom_fields');
    const canManageEbay = hasPermission(user, 'can_manage_ebay');
    const canAccessCustomApps = hasPermission(user, 'can_access_replit');
    const segmentsNotVisibleForReceiver = canManageCustomFields
      ? ['channel-receiver']
      : ['custom_fields', 'channel-receiver'];

    let hiddenSegments = isReceiver ? segmentsNotVisibleForReceiver : this.getHiddenSegments();

    // Hide eBay if user doesn't have permission
    if (!canManageEbay) {
      hiddenSegments = [...hiddenSegments, 'ebay'];
    }

    const navigation = navigationBarData.filter(({ key }) => !hiddenSegments.includes(key));
    const customApps = canAccessCustomApps ? this.getCustomAppsForProduct() : [];

    let dividerKey: string;
    if (canManageEbay && navigation.find(nav => nav.key === 'ebay')) {
      dividerKey = 'ebay';
    } else if (navigation.find(nav => nav.key === 'channel-receiver')) {
      dividerKey = 'channel-receiver';
    } else {
      dividerKey = 'insights';
    }

    const navigationItems = navigation.map(nav => ({
      className: classNames({ 'sidebar-brand-products__divider': nav.key === dividerKey }),
      key: `${nav.link}`,
      onClick: () => this.handleMenuClick(nav.key),
      label: (
        <Link
          to={{
            ...location,
            pathname: `${nav.link}`,
          }}
        >
          <div className="sidebar-brand-products__menu-item">
            <span>{nav.value}</span>
            {this.scoreStyleClass(nav.id)}
          </div>
        </Link>
      ),
    }));

    const customAppItems = customApps.map(app => ({
      key: `custom-app/${app.id}`,
      onClick: () => this.handleMenuClick(`custom-app-${app.id}`),
      label: (
        <Link
          to={{
            ...location,
            pathname: `custom-app/${app.id}`,
          }}
        >
          <div className="sidebar-brand-products__menu-item">
            <span>{app.displayName}</span>
          </div>
        </Link>
      ),
    }));

    const sideBarItems = [...navigationItems, ...customAppItems];

    // Determine selected key - handle custom-app routes properly
    const pathParts = location.pathname.split('/');
    let selectedKey = pathParts.pop() || '';
    if (pathParts[pathParts.length - 1] === 'custom-app') {
      selectedKey = `custom-app/${selectedKey}`;
    }

    return (
      <Sider theme="light" className="sidebar-brand-products">
        <Menu
          defaultSelectedKeys={['1']}
          selectedKeys={[selectedKey]}
          mode="inline"
          items={sideBarItems}
        />
      </Sider>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => {
  const selectedItems = getSelectedItems(state);
  return {
    segmentScores: state.items.analyses.segmentScores,
    selectedItems,
    isReceiver: state.user.user && isReceiver(state.user.user),
    user: state.user.user,
    roles: state.user.roles,
    integrations: state.tools.replit.integrations,
    customAppConfigs: state.customAppsSettings.configs,
  };
};

export default withRouter(connect(mapStateToProps)(BrandProductsSideBar));
